import React, { useState, useEffect } from 'react';
import {
  Box, Typography, IconButton, Chip, Grid, TableRow, TableCell,
  FormControl, InputLabel, Select, MenuItem, TablePagination,
  TextField, CircularProgress, Alert, Tooltip, useTheme
} from '@mui/material';
import {
  Refresh, TrendingUp, TrendingDown, AccountBalanceWallet, Payment,
  Receipt, Search, AttachMoney, CreditCard, Schedule,
  CheckCircle, Cancel, MoneyOff
} from '@mui/icons-material';
import api from '../../utils/api';
import {
  SectionHeader, GlassPaper, GlassTabs, GlassTable,
  GradientButton, StatCard, ShimmerTableRows, EmptyState
} from './ui';
import { glass } from './ui/glassTokens';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function FinancePanel() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const g = glass(theme);

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Summary data
  const [summary, setSummary] = useState(null);

  // Payments state
  const [payments, setPayments] = useState([]);
  const [paymentFilters, setPaymentFilters] = useState({
    status: '',
    payment_method: '',
    search: '',
  });
  const [paymentPage, setPaymentPage] = useState(0);
  const [paymentRowsPerPage, setPaymentRowsPerPage] = useState(10);
  const [paymentTotal, setPaymentTotal] = useState(0);

  // Wallet transactions state
  const [transactions, setTransactions] = useState([]);
  const [transactionFilters, setTransactionFilters] = useState({
    type: '',
    reference_type: '',
    search: '',
  });
  const [transactionPage, setTransactionPage] = useState(0);
  const [transactionRowsPerPage, setTransactionRowsPerPage] = useState(10);
  const [transactionTotal, setTransactionTotal] = useState(0);

  // Wallets state
  const [wallets, setWallets] = useState([]);
  const [walletFilters, setWalletFilters] = useState({
    role: '',
    search: '',
  });
  const [walletPage, setWalletPage] = useState(0);
  const [walletRowsPerPage, setWalletRowsPerPage] = useState(10);
  const [walletTotal, setWalletTotal] = useState(0);
  const [walletTotals, setWalletTotals] = useState({ total_balance: 0, total_wallets: 0 });

  // Razorpay payments state
  const [razorpayPayments, setRazorpayPayments] = useState([]);
  const [razorpayFilters, setRazorpayFilters] = useState({
    status: '',
    purpose: '',
    method: '',
    search: '',
  });
  const [razorpayPage, setRazorpayPage] = useState(0);
  const [razorpayRowsPerPage, setRazorpayRowsPerPage] = useState(10);
  const [razorpayTotal, setRazorpayTotal] = useState(0);
  const [razorpaySummary, setRazorpaySummary] = useState({});

  useEffect(() => {
    fetchSummary();
  }, []);

  useEffect(() => {
    if (activeTab === 1) fetchPayments();
    if (activeTab === 2) fetchTransactions();
    if (activeTab === 3) fetchWallets();
    if (activeTab === 4) fetchRazorpayPayments();
  }, [activeTab, paymentPage, paymentRowsPerPage, transactionPage, transactionRowsPerPage, walletPage, walletRowsPerPage, razorpayPage, razorpayRowsPerPage]);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/finance/summary');
      setSummary(response.data.data);
    } catch (err) {
      setError('Failed to load financial summary');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      const params = new URLSearchParams({
        page: paymentPage + 1,
        limit: paymentRowsPerPage,
      });
      if (paymentFilters.status) params.append('status', paymentFilters.status);
      if (paymentFilters.payment_method) params.append('payment_method', paymentFilters.payment_method);
      if (paymentFilters.search) params.append('search', paymentFilters.search);

      const response = await api.get(`/admin/finance/payments?${params}`);
      setPayments(response.data.data.payments || []);
      setPaymentTotal(response.data.data.pagination?.total || 0);
    } catch (err) {
      console.error('Failed to load payments:', err);
    }
  };

  const fetchTransactions = async () => {
    try {
      const params = new URLSearchParams({
        page: transactionPage + 1,
        limit: transactionRowsPerPage,
      });
      if (transactionFilters.type) params.append('type', transactionFilters.type);
      if (transactionFilters.reference_type) params.append('reference_type', transactionFilters.reference_type);
      if (transactionFilters.search) params.append('search', transactionFilters.search);

      const response = await api.get(`/admin/finance/wallet-transactions?${params}`);
      setTransactions(response.data.data.transactions || []);
      setTransactionTotal(response.data.data.pagination?.total || 0);
    } catch (err) {
      console.error('Failed to load transactions:', err);
    }
  };

  const fetchWallets = async () => {
    try {
      const params = new URLSearchParams({
        page: walletPage + 1,
        limit: walletRowsPerPage,
      });
      if (walletFilters.role) params.append('role', walletFilters.role);
      if (walletFilters.search) params.append('search', walletFilters.search);

      const response = await api.get(`/admin/finance/wallets?${params}`);
      setWallets(response.data.data.wallets || []);
      setWalletTotal(response.data.data.pagination?.total || 0);
      setWalletTotals(response.data.data.totals || {});
    } catch (err) {
      console.error('Failed to load wallets:', err);
    }
  };

  const fetchRazorpayPayments = async () => {
    try {
      const params = new URLSearchParams({
        page: razorpayPage + 1,
        limit: razorpayRowsPerPage,
      });
      if (razorpayFilters.status) params.append('status', razorpayFilters.status);
      if (razorpayFilters.purpose) params.append('purpose', razorpayFilters.purpose);
      if (razorpayFilters.method) params.append('method', razorpayFilters.method);
      if (razorpayFilters.search) params.append('search', razorpayFilters.search);

      const response = await api.get(`/admin/finance/razorpay-payments?${params}`);
      setRazorpayPayments(response.data.data.payments || []);
      setRazorpayTotal(response.data.data.pagination?.total || 0);
      setRazorpaySummary(response.data.data.summary || {});
    } catch (err) {
      console.error('Failed to load Razorpay payments:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const statusChipSx = (color) => ({
    fontSize: '0.7rem',
    fontWeight: 600,
    height: 22,
    background: color === 'success' ? 'rgba(16,185,129,0.15)' :
      color === 'error' ? 'rgba(244,63,94,0.15)' :
      color === 'warning' ? 'rgba(245,158,11,0.15)' :
      color === 'info' ? 'rgba(34,211,238,0.15)' :
      isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.07)',
    color: color === 'success' ? '#10B981' :
      color === 'error' ? '#F43F5E' :
      color === 'warning' ? '#F59E0B' :
      color === 'info' ? '#22D3EE' :
      isDark ? 'rgba(255,255,255,0.6)' : 'rgba(15,23,42,0.6)',
    border: 'none',
  });

  const getPaymentStatusChip = (status) => {
    const config = {
      pending: { color: 'warning', icon: <Schedule sx={{ fontSize: '0.75rem !important' }} /> },
      completed: { color: 'success', icon: <CheckCircle sx={{ fontSize: '0.75rem !important' }} /> },
      failed: { color: 'error', icon: <Cancel sx={{ fontSize: '0.75rem !important' }} /> },
      refunded: { color: 'info', icon: <MoneyOff sx={{ fontSize: '0.75rem !important' }} /> },
      cancelled: { color: 'default', icon: <Cancel sx={{ fontSize: '0.75rem !important' }} /> },
    };
    const c = config[status] || { color: 'default' };
    return <Chip label={status} size="small" icon={c.icon} sx={statusChipSx(c.color)} />;
  };

  const getTransactionTypeChip = (type) => (
    <Chip
      label={type.toUpperCase()}
      size="small"
      icon={type === 'credit'
        ? <TrendingUp sx={{ fontSize: '0.75rem !important' }} />
        : <TrendingDown sx={{ fontSize: '0.75rem !important' }} />}
      sx={statusChipSx(type === 'credit' ? 'success' : 'error')}
    />
  );

  const getReferenceTypeLabel = (refType) => {
    const labels = {
      topup: 'Top-up',
      booking: 'Booking',
      booking_payment: 'Booking Payment',
      refund: 'Refund',
      withdrawal: 'Withdrawal',
      withdrawal_refund: 'Withdrawal Refund',
      referral: 'Referral',
    };
    return labels[refType] || refType || 'Other';
  };

  const selectSx = {
    '& .MuiOutlinedInput-root': {
      ...g.surface,
      '& fieldset': { border: 'none' },
    },
    '& .MuiInputLabel-root': {
      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
    },
    '& .MuiSelect-select': {
      color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)',
    },
    '& .MuiSvgIcon-root': {
      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
    },
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      ...g.surface,
      '& fieldset': { border: 'none' },
      color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)',
    },
    '& .MuiInputLabel-root': {
      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
    },
    '& .MuiSvgIcon-root': {
      color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)',
    },
  };

  const paginationSx = {
    color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(15,23,42,0.6)',
    '& .MuiSelect-select': { color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' },
    '& .MuiSvgIcon-root': { color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' },
    '& .MuiButtonBase-root': { color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' },
  };

  const renderSummary = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress sx={{ color: '#6366F1' }} />
        </Box>
      );
    }

    if (!summary) return null;

    return (
      <Box>
        {/* Overview KPI Cards */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Total Revenue"
              value={summary.overview?.totalRevenueDisplay || '₹0'}
              icon={<AttachMoney />}
              iconIndex={0}
              index={0}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Total Wallet Balance"
              value={summary.overview?.totalWalletBalanceDisplay || '₹0'}
              icon={<AccountBalanceWallet />}
              iconIndex={2}
              index={1}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Pending Withdrawals"
              value={summary.overview?.pendingWithdrawalsDisplay || '₹0'}
              icon={<Payment />}
              iconIndex={3}
              index={2}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Active Wallets"
              value={summary.overview?.activeWallets || 0}
              icon={<CreditCard />}
              iconIndex={1}
              index={3}
            />
          </Grid>
        </Grid>

        {/* Payment Status Summary */}
        <Typography
          variant="overline"
          sx={{
            fontWeight: 700,
            letterSpacing: '0.1em',
            color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.45)',
            mb: 2,
            display: 'block',
          }}
        >
          Payment Status
        </Typography>
        <Grid container spacing={2} mb={4}>
          {['pending', 'completed', 'failed', 'refunded', 'cancelled'].map((status) => (
            <Grid item xs={6} sm={4} md={2.4} key={status}>
              <GlassPaper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h5" fontWeight="700" sx={{
                  color: status === 'completed' ? '#10B981' :
                    status === 'pending' ? '#F59E0B' :
                    status === 'failed' || status === 'cancelled' ? '#F43F5E' : '#22D3EE',
                }}>
                  {summary.payments?.[status]?.count || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)', textTransform: 'capitalize' }}>
                  {status}
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(15,23,42,0.4)' }}>
                  ₹{((summary.payments?.[status]?.amount || 0) / 100).toFixed(0)}
                </Typography>
              </GlassPaper>
            </Grid>
          ))}
        </Grid>

        {/* Wallet Activity */}
        <Typography
          variant="overline"
          sx={{
            fontWeight: 700,
            letterSpacing: '0.1em',
            color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.45)',
            mb: 2,
            display: 'block',
          }}
        >
          Wallet Activity
        </Typography>
        <Grid container spacing={2} mb={4}>
          <Grid item xs={6} sm={3}>
            <GlassPaper sx={{ p: 2, textAlign: 'center' }}>
              <TrendingUp sx={{ color: '#10B981', fontSize: 32, mb: 0.5 }} />
              <Typography variant="h5" fontWeight="700" sx={{ color: '#10B981' }}>
                ₹{((summary.wallet?.totalCredits || 0) / 100).toFixed(0)}
              </Typography>
              <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}>Total Credits</Typography>
            </GlassPaper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <GlassPaper sx={{ p: 2, textAlign: 'center' }}>
              <TrendingDown sx={{ color: '#F43F5E', fontSize: 32, mb: 0.5 }} />
              <Typography variant="h5" fontWeight="700" sx={{ color: '#F43F5E' }}>
                ₹{((summary.wallet?.totalDebits || 0) / 100).toFixed(0)}
              </Typography>
              <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}>Total Debits</Typography>
            </GlassPaper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <GlassPaper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight="700" sx={{ color: '#6366F1' }}>
                {summary.wallet?.topups?.count || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}>Top-ups</Typography>
              <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(15,23,42,0.4)' }}>
                ₹{((summary.wallet?.topups?.amount || 0) / 100).toFixed(0)}
              </Typography>
            </GlassPaper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <GlassPaper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight="700" sx={{ color: '#8B5CF6' }}>
                {summary.wallet?.bookings?.count || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}>Bookings</Typography>
              <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(15,23,42,0.4)' }}>
                ₹{((summary.wallet?.bookings?.amount || 0) / 100).toFixed(0)}
              </Typography>
            </GlassPaper>
          </Grid>
        </Grid>

        {/* Withdrawal Summary */}
        <Typography
          variant="overline"
          sx={{
            fontWeight: 700,
            letterSpacing: '0.1em',
            color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.45)',
            mb: 2,
            display: 'block',
          }}
        >
          Withdrawal Status
        </Typography>
        <Grid container spacing={2}>
          {['pending', 'processing', 'completed', 'rejected'].map((status) => (
            <Grid item xs={6} sm={3} key={status}>
              <GlassPaper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h5" fontWeight="700" sx={{
                  color: status === 'completed' ? '#10B981' :
                    status === 'pending' ? '#F59E0B' :
                    status === 'processing' ? '#22D3EE' : '#F43F5E',
                }}>
                  {summary.withdrawals?.[status]?.count || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)', textTransform: 'capitalize' }}>
                  {status}
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(15,23,42,0.4)' }}>
                  ₹{((summary.withdrawals?.[status]?.amount || 0) / 100).toFixed(0)}
                </Typography>
              </GlassPaper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const renderPayments = () => (
    <Box>
      <GlassPaper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Search by name/email"
              value={paymentFilters.search}
              onChange={(e) => setPaymentFilters({ ...paymentFilters, search: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && fetchPayments()}
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <IconButton size="small" onClick={fetchPayments} sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }}>
                    <Search fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small" sx={selectSx}>
              <InputLabel>Status</InputLabel>
              <Select
                value={paymentFilters.status}
                label="Status"
                onChange={(e) => {
                  setPaymentFilters({ ...paymentFilters, status: e.target.value });
                  setPaymentPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="refunded">Refunded</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small" sx={selectSx}>
              <InputLabel>Method</InputLabel>
              <Select
                value={paymentFilters.payment_method}
                label="Method"
                onChange={(e) => {
                  setPaymentFilters({ ...paymentFilters, payment_method: e.target.value });
                  setPaymentPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="wallet">Wallet</MenuItem>
                <MenuItem value="upi">UPI</MenuItem>
                <MenuItem value="card">Card</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="net_banking">Net Banking</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <GradientButton variant="ghost" fullWidth startIcon={<Refresh />} onClick={fetchPayments}>
              Refresh
            </GradientButton>
          </Grid>
        </Grid>
      </GlassPaper>

      <GlassTable
        columns={[
          { label: 'Student' },
          { label: 'Amount' },
          { label: 'Status' },
          { label: 'Method' },
          { label: 'Library' },
          { label: 'Date' },
        ]}
      >
        {payments.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} sx={{ border: 0, p: 0 }}>
              <EmptyState
                icon={<Payment />}
                iconIndex={0}
                title="No payments found"
                description="Payments will appear here once students make transactions."
              />
            </TableCell>
          </TableRow>
        ) : (
          payments.map((payment) => (
            <TableRow key={payment.id}>
              <TableCell>
                <Typography variant="body2" fontWeight="600" sx={{ color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)' }}>
                  {payment.student?.name}
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>
                  {payment.student?.email}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="700" sx={{ color: '#6366F1' }}>
                  {payment.amount_display}
                </Typography>
              </TableCell>
              <TableCell>{getPaymentStatusChip(payment.status)}</TableCell>
              <TableCell>
                <Chip label={payment.payment_method?.toUpperCase() || 'N/A'} size="small" variant="outlined"
                  sx={{ fontSize: '0.7rem', borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(15,23,42,0.15)', color: 'inherit' }} />
              </TableCell>
              <TableCell>{payment.library?.name || '-'}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}>
                {formatDate(payment.created_at)}
              </TableCell>
            </TableRow>
          ))
        )}
      </GlassTable>
      <TablePagination
        component="div"
        count={paymentTotal}
        page={paymentPage}
        onPageChange={(e, newPage) => setPaymentPage(newPage)}
        rowsPerPage={paymentRowsPerPage}
        onRowsPerPageChange={(e) => {
          setPaymentRowsPerPage(parseInt(e.target.value, 10));
          setPaymentPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={paginationSx}
      />
    </Box>
  );

  const renderTransactions = () => (
    <Box>
      <GlassPaper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Search by name/email"
              value={transactionFilters.search}
              onChange={(e) => setTransactionFilters({ ...transactionFilters, search: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && fetchTransactions()}
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <IconButton size="small" onClick={fetchTransactions} sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }}>
                    <Search fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small" sx={selectSx}>
              <InputLabel>Type</InputLabel>
              <Select
                value={transactionFilters.type}
                label="Type"
                onChange={(e) => {
                  setTransactionFilters({ ...transactionFilters, type: e.target.value });
                  setTransactionPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="credit">Credit</MenuItem>
                <MenuItem value="debit">Debit</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small" sx={selectSx}>
              <InputLabel>Category</InputLabel>
              <Select
                value={transactionFilters.reference_type}
                label="Category"
                onChange={(e) => {
                  setTransactionFilters({ ...transactionFilters, reference_type: e.target.value });
                  setTransactionPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="topup">Top-up</MenuItem>
                <MenuItem value="booking">Booking</MenuItem>
                <MenuItem value="booking_payment">Booking Payment</MenuItem>
                <MenuItem value="refund">Refund</MenuItem>
                <MenuItem value="withdrawal">Withdrawal</MenuItem>
                <MenuItem value="withdrawal_refund">Withdrawal Refund</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <GradientButton variant="ghost" fullWidth startIcon={<Refresh />} onClick={fetchTransactions}>
              Refresh
            </GradientButton>
          </Grid>
        </Grid>
      </GlassPaper>

      <GlassTable
        columns={[
          { label: 'User' },
          { label: 'Type' },
          { label: 'Amount' },
          { label: 'Category' },
          { label: 'Description' },
          { label: 'Balance After' },
          { label: 'Date' },
        ]}
      >
        {transactions.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} sx={{ border: 0, p: 0 }}>
              <EmptyState
                icon={<TrendingUp />}
                iconIndex={2}
                title="No transactions found"
              />
            </TableCell>
          </TableRow>
        ) : (
          transactions.map((txn) => (
            <TableRow key={txn.id}>
              <TableCell>
                <Typography variant="body2" fontWeight="600" sx={{ color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)' }}>
                  {txn.user?.name || 'N/A'}
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>
                  {txn.user?.email}
                </Typography>
              </TableCell>
              <TableCell>{getTransactionTypeChip(txn.type)}</TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="700" sx={{
                  color: txn.type === 'credit' ? '#10B981' : '#F43F5E',
                }}>
                  {txn.amount_display}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip label={getReferenceTypeLabel(txn.reference_type)} size="small" variant="outlined"
                  sx={{ fontSize: '0.7rem', borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(15,23,42,0.15)', color: 'inherit' }} />
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {txn.description || '-'}
                </Typography>
              </TableCell>
              <TableCell>{txn.balance_after_display}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}>
                {formatDate(txn.created_at)}
              </TableCell>
            </TableRow>
          ))
        )}
      </GlassTable>
      <TablePagination
        component="div"
        count={transactionTotal}
        page={transactionPage}
        onPageChange={(e, newPage) => setTransactionPage(newPage)}
        rowsPerPage={transactionRowsPerPage}
        onRowsPerPageChange={(e) => {
          setTransactionRowsPerPage(parseInt(e.target.value, 10));
          setTransactionPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={paginationSx}
      />
    </Box>
  );

  const renderWallets = () => (
    <Box>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6}>
          <StatCard
            label="Total Balance (All Wallets)"
            value={walletTotals.total_balance_display || '₹0'}
            iconIndex={0}
            icon={<AccountBalanceWallet />}
          />
        </Grid>
        <Grid item xs={6}>
          <StatCard
            label="Total Wallets"
            value={walletTotals.total_wallets || 0}
            iconIndex={1}
            icon={<CreditCard />}
          />
        </Grid>
      </Grid>

      <GlassPaper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              size="small"
              label="Search by name/email"
              value={walletFilters.search}
              onChange={(e) => setWalletFilters({ ...walletFilters, search: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && fetchWallets()}
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <IconButton size="small" onClick={fetchWallets} sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }}>
                    <Search fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <FormControl fullWidth size="small" sx={selectSx}>
              <InputLabel>Role</InputLabel>
              <Select
                value={walletFilters.role}
                label="Role"
                onChange={(e) => {
                  setWalletFilters({ ...walletFilters, role: e.target.value });
                  setWalletPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="student">Students</MenuItem>
                <MenuItem value="librarian">Librarians</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <GradientButton variant="ghost" fullWidth startIcon={<Refresh />} onClick={fetchWallets}>
              Refresh
            </GradientButton>
          </Grid>
        </Grid>
      </GlassPaper>

      <GlassTable
        columns={[
          { label: 'User' },
          { label: 'Role' },
          { label: 'Balance' },
          { label: 'Status' },
          { label: 'Created' },
        ]}
      >
        {wallets.length === 0 ? (
          <TableRow>
            <TableCell colSpan={5} sx={{ border: 0, p: 0 }}>
              <EmptyState
                icon={<AccountBalanceWallet />}
                iconIndex={1}
                title="No wallets found"
              />
            </TableCell>
          </TableRow>
        ) : (
          wallets.map((wallet) => (
            <TableRow key={wallet.id}>
              <TableCell>
                <Typography variant="body2" fontWeight="600" sx={{ color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)' }}>
                  {wallet.user?.name}
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>
                  {wallet.user?.email}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={wallet.user?.role}
                  size="small"
                  sx={{
                    ...statusChipSx(wallet.user?.role === 'librarian' ? 'info' : 'default'),
                    textTransform: 'capitalize',
                  }}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="700" sx={{ color: '#6366F1' }}>
                  {wallet.balance_display}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={wallet.is_active ? 'Active' : 'Inactive'}
                  size="small"
                  sx={statusChipSx(wallet.is_active ? 'success' : 'default')}
                />
              </TableCell>
              <TableCell sx={{ fontSize: '0.8rem', color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}>
                {formatDate(wallet.created_at)}
              </TableCell>
            </TableRow>
          ))
        )}
      </GlassTable>
      <TablePagination
        component="div"
        count={walletTotal}
        page={walletPage}
        onPageChange={(e, newPage) => setWalletPage(newPage)}
        rowsPerPage={walletRowsPerPage}
        onRowsPerPageChange={(e) => {
          setWalletRowsPerPage(parseInt(e.target.value, 10));
          setWalletPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={paginationSx}
      />
    </Box>
  );

  const getRazorpayStatusChip = (status) => {
    const config = {
      created: { color: 'warning', label: 'Created' },
      authorized: { color: 'info', label: 'Authorized' },
      captured: { color: 'success', label: 'Captured' },
      refunded: { color: 'default', label: 'Refunded' },
      failed: { color: 'error', label: 'Failed' },
    };
    const c = config[status] || { color: 'default', label: status };
    return <Chip label={c.label} size="small" sx={statusChipSx(c.color)} />;
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      upi: 'UPI',
      card: 'Card',
      netbanking: 'Net Banking',
      wallet: 'Wallet',
      emi: 'EMI',
    };
    return labels[method] || method || 'N/A';
  };

  const renderRazorpayPayments = () => (
    <Box>
      <Grid container spacing={2} mb={3}>
        {['captured', 'created', 'failed', 'refunded'].map((status, idx) => (
          <Grid item xs={6} sm={3} key={status}>
            <StatCard
              label={status.charAt(0).toUpperCase() + status.slice(1)}
              value={razorpaySummary[status]?.count || 0}
              suffix={` / ₹${((razorpaySummary[status]?.amount || 0) / 100).toFixed(0)}`}
              iconIndex={idx}
              icon={<CreditCard />}
            />
          </Grid>
        ))}
      </Grid>

      <GlassPaper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Search by name/email"
              value={razorpayFilters.search}
              onChange={(e) => setRazorpayFilters({ ...razorpayFilters, search: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && fetchRazorpayPayments()}
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <IconButton size="small" onClick={fetchRazorpayPayments} sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }}>
                    <Search fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small" sx={selectSx}>
              <InputLabel>Status</InputLabel>
              <Select
                value={razorpayFilters.status}
                label="Status"
                onChange={(e) => {
                  setRazorpayFilters({ ...razorpayFilters, status: e.target.value });
                  setRazorpayPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="created">Created</MenuItem>
                <MenuItem value="authorized">Authorized</MenuItem>
                <MenuItem value="captured">Captured</MenuItem>
                <MenuItem value="refunded">Refunded</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small" sx={selectSx}>
              <InputLabel>Purpose</InputLabel>
              <Select
                value={razorpayFilters.purpose}
                label="Purpose"
                onChange={(e) => {
                  setRazorpayFilters({ ...razorpayFilters, purpose: e.target.value });
                  setRazorpayPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="wallet_topup">Wallet Top-up</MenuItem>
                <MenuItem value="booking">Booking</MenuItem>
                <MenuItem value="subscription">Subscription</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small" sx={selectSx}>
              <InputLabel>Method</InputLabel>
              <Select
                value={razorpayFilters.method}
                label="Method"
                onChange={(e) => {
                  setRazorpayFilters({ ...razorpayFilters, method: e.target.value });
                  setRazorpayPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="upi">UPI</MenuItem>
                <MenuItem value="card">Card</MenuItem>
                <MenuItem value="netbanking">Net Banking</MenuItem>
                <MenuItem value="wallet">Wallet</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <GradientButton variant="ghost" fullWidth startIcon={<Refresh />} onClick={fetchRazorpayPayments}>
              Refresh
            </GradientButton>
          </Grid>
        </Grid>
      </GlassPaper>

      <GlassTable
        columns={[
          { label: 'User' },
          { label: 'Amount' },
          { label: 'Status' },
          { label: 'Method' },
          { label: 'Purpose' },
          { label: 'Order ID' },
          { label: 'Date' },
        ]}
      >
        {razorpayPayments.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} sx={{ border: 0, p: 0 }}>
              <EmptyState
                icon={<CreditCard />}
                iconIndex={0}
                title="No Razorpay payments found"
              />
            </TableCell>
          </TableRow>
        ) : (
          razorpayPayments.map((payment) => (
            <TableRow key={payment.id}>
              <TableCell>
                <Typography variant="body2" fontWeight="600" sx={{ color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)' }}>
                  {payment.user?.name}
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>
                  {payment.user?.email}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="700" sx={{ color: '#6366F1' }}>
                  {payment.amount_display}
                </Typography>
                {payment.fee_display && (
                  <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }}>
                    Fee: {payment.fee_display}
                  </Typography>
                )}
              </TableCell>
              <TableCell>{getRazorpayStatusChip(payment.status)}</TableCell>
              <TableCell>
                <Box>
                  <Chip label={getPaymentMethodLabel(payment.method)} size="small" variant="outlined"
                    sx={{ fontSize: '0.7rem', borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(15,23,42,0.15)', color: 'inherit' }} />
                  {payment.vpa && (
                    <Typography variant="caption" display="block" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }}>
                      {payment.vpa}
                    </Typography>
                  )}
                  {payment.card_last4 && (
                    <Typography variant="caption" display="block" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }}>
                      **** {payment.card_last4} ({payment.card_network})
                    </Typography>
                  )}
                  {payment.bank && (
                    <Typography variant="caption" display="block" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }}>
                      {payment.bank}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  label={payment.purpose?.replace('_', ' ').toUpperCase() || 'N/A'}
                  size="small"
                  sx={statusChipSx('default')}
                />
              </TableCell>
              <TableCell>
                <Tooltip title={payment.razorpay_payment_id || 'No payment ID yet'}>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}>
                    {payment.razorpay_order_id?.slice(0, 15)}...
                  </Typography>
                </Tooltip>
                {payment.webhook_received && (
                  <Chip label="Webhook" size="small" sx={{ ml: 1, ...statusChipSx('success') }} />
                )}
              </TableCell>
              <TableCell sx={{ fontSize: '0.8rem', color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}>
                {formatDate(payment.created_at)}
              </TableCell>
            </TableRow>
          ))
        )}
      </GlassTable>
      <TablePagination
        component="div"
        count={razorpayTotal}
        page={razorpayPage}
        onPageChange={(e, newPage) => setRazorpayPage(newPage)}
        rowsPerPage={razorpayRowsPerPage}
        onRowsPerPageChange={(e) => {
          setRazorpayRowsPerPage(parseInt(e.target.value, 10));
          setRazorpayPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={paginationSx}
      />
    </Box>
  );

  const tabItems = [
    { value: 0, label: 'Overview', icon: <Receipt /> },
    { value: 1, label: 'Payments', icon: <Payment /> },
    { value: 2, label: 'Wallet Txns', icon: <TrendingUp /> },
    { value: 3, label: 'Wallets', icon: <AccountBalanceWallet /> },
    { value: 4, label: 'Razorpay', icon: <CreditCard /> },
  ];

  return (
    <Box>
      <SectionHeader
        eyebrow="Platform"
        title="Finance Dashboard"
        action={
          <GradientButton variant="ghost" startIcon={<Refresh />} onClick={fetchSummary}>
            Refresh
          </GradientButton>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <GlassTabs
        tabs={tabItems}
        value={activeTab}
        onChange={(e, val) => setActiveTab(val)}
        sx={{ mb: 3 }}
      />

      <TabPanel value={activeTab} index={0}>
        {renderSummary()}
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        {renderPayments()}
      </TabPanel>
      <TabPanel value={activeTab} index={2}>
        {renderTransactions()}
      </TabPanel>
      <TabPanel value={activeTab} index={3}>
        {renderWallets()}
      </TabPanel>
      <TabPanel value={activeTab} index={4}>
        {renderRazorpayPayments()}
      </TabPanel>
    </Box>
  );
}

export default FinancePanel;
